import React, {
  useCallback, memo, useMemo, useState,
} from 'react';

import {
  arrayOf, string, shape, func, bool,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, TextField } from '@picsio/ui';
import { ControlledVocabulary, Label } from '@picsio/icons';
import cn from 'classnames';

import KeywordsDropdown from '../keywordsDropdown';
import ReadOnlyFieldTooltip from './helpers/ReadOnlyFieldTooltip';
import { productFieldInputProps } from './constants';
import l10n from '../../shared/strings';
import parentStyles from './styles.module.scss';
import { changeTree } from '../../store/actions/main';
import { getKeywordNameFromPath } from '../../store/helpers/keywords';
import { setSearchRoute } from '../../helpers/history';
import Logger from '../../services/Logger';
import * as UtilsCollections from '../../store/utils/collections';

const getKeywordLabel = ({ title, path } = {}) => title || (path ? getKeywordNameFromPath(path) : '');

function Keywords({
  productId,
  keywords = [],
  onAddKeyword,
  onRemoveKeyword,
  readOnly,
  readOnlyTooltip,
}) {
  const dispatch = useDispatch();
  const isCreatableKeywords = useSelector((state) => {
    const isAddKeywordsOutsideVocabulary = state.user.role.permissions.addKeywordsOutsideVocabulary;
    const { isKeywordsActionsAllowed } = state.user;
    return isAddKeywordsOutsideVocabulary || isKeywordsActionsAllowed;
  });
  const openedTree = useSelector((state) => state.main?.openedTree);
  const isCVEnabled = useSelector((state) => state.user?.team?.policies?.useKeywordsControlledVocabulary);
  const [isBusy, setBusy] = useState(false);

  const handleAttachKeyword = useCallback(async (keyword, logEvent = true) => {
    /** keyword can be array, when user attach it with comma (,) */
    const isArray = Array.isArray(keyword);
    const keywordName = isArray ? keyword.map(({ title }) => title).join(',') : keyword.title;

    if (logEvent) {
      Logger.log('User', 'ProductKeywordsAdd', {
        keyword: isArray ? keyword.map(({ _id, title }) => `${_id || '<NEW>'} | ${title}`).join(' ; ') : `${keyword._id || '<NEW>'} | ${keyword.title}`,
        product: productId,
        keywordCount: isArray ? keyword.length : 1,
      });
    }
    setBusy(true);
    await onAddKeyword(keywordName);
    setBusy(false);
  }, [onAddKeyword, productId]);

  const handleDetachKeyword = useCallback(async (item) => {
    setBusy(true);
    await onRemoveKeyword([item._id]);
    setBusy(false);
  }, [onRemoveKeyword]);

  const handleClickKeyword = useCallback((keywordId) => {
    /** Navigate to keyword */
    if (openedTree !== 'keywords') dispatch(changeTree('keywords'));

    setSearchRoute({
      collectionIds: UtilsCollections.getRootId(),
      keywords: keywordId,
    });
  }, [dispatch, openedTree]);

  const dropdownIcon = useCallback(() => (
    <Icon>
      <Choose>
        <When condition={isCVEnabled}>
          <ControlledVocabulary />
        </When>
        <Otherwise>
          <Label />
        </Otherwise>
      </Choose>
    </Icon>
  ), [isCVEnabled]);

  const readOnlyValue = useMemo(
    () => keywords.map(getKeywordLabel).filter(Boolean).join(', '),
    [keywords],
  );

  if (readOnly) {
    return (
      <ReadOnlyFieldTooltip tooltip={readOnlyTooltip}>
        <div className={cn(parentStyles.row, 'm-t-20')}>
          <TextField
            value={readOnlyValue}
            disabled={readOnly}
            label="Keywords"
            inputProps={productFieldInputProps}
          />
        </div>
      </ReadOnlyFieldTooltip>
    );
  }

  return (
    <KeywordsDropdown
      placeholder={l10n.DROPDOWN.placeholderKeywords}
      placeholderIcon="emptyKeywords"
      icon={dropdownIcon}
      filterText={isCVEnabled ? l10n.DROPDOWN.chooseKeywordsCV : l10n.DROPDOWN.chooseKeywords}
      createText={l10n.DROPDOWN.createKeyword}
      createPlaceholderText={l10n.DROPDOWN.placeholderKeywordsCreate}
      checkedItems={keywords}
      canCreate={isCreatableKeywords}
      showAddButton={false}
      disabled={isBusy}
      onItemClickHandler={handleClickKeyword}
      onCheckedHandler={handleAttachKeyword}
      onUncheckedHandler={handleDetachKeyword}
    />
  );
}

Keywords.defaultProps = {
  keywords: [],
  readOnly: false,
  readOnlyTooltip: null,
  onAddKeyword: null,
  onRemoveKeyword: null,
};

Keywords.propTypes = {
  productId: string.isRequired,
  keywords: arrayOf(shape({
    _id: string,
    title: string,
    path: string,
  })),
  onAddKeyword: func,
  onRemoveKeyword: func,
  readOnly: bool,
  readOnlyTooltip: string,
};

export default memo(Keywords);
